import { ModerationChoices } from '@/components/photovogue/moderation-choices/component';
import type { ModerationChoice } from '@/hooks/use-page-moderation';
import { Checkbox } from '@repo/ui/components/ui/checkbox';
import { cn } from '@repo/ui/lib/utils';
import type { Photo } from '@/services/photovogue';
import { getModerationPreviewUrl } from '@/services/photovogue/moderation-image';
import { PhotoStateBadge } from '@/components/photovogue/state-badge/component';

export interface PhotoCardProps {
  photo: Photo;
  selectable: boolean;
  moderating?: boolean;
  disabled?: boolean;
  ignoredIds?: string[];
  onChoice?: (id: string, choice: ModerationChoice) => void;
  onPreview?: (photo: Photo) => void;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onOpen: (photo: Photo) => void;
}

export const PhotoCard = ({
  photo,
  selectable,
  moderating = false,
  disabled = false,
  ignoredIds = [],
  onChoice,
  onPreview,
  selected,
  onToggleSelect,
  onOpen,
}: PhotoCardProps) => {
  const handleClick = () => {
    if (onPreview) onPreview(photo);
    else if (selectable) onToggleSelect(photo.id);
    else onOpen(photo);
  };

  return (
    <div
      className={cn(
        'group relative overflow-hidden rounded-lg border bg-white transition-shadow hover:shadow-md',
        moderating
          ? ignoredIds.includes(photo.id)
            ? 'bg-zinc-50'
            : selected
              ? 'bg-green-50'
              : 'bg-red-50'
          : selected && 'ring-2 ring-zinc-900',
      )}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={handleClick}
        className="block w-full text-left"
      >
        <div className="relative aspect-square overflow-hidden bg-zinc-950">
          <img
            src={getModerationPreviewUrl(photo)}
            alt={photo.title}
            loading="lazy"
            className="h-full w-full object-contain"
          />
          <div className="absolute left-2 top-2">
            <PhotoStateBadge state={photo.state} />
          </div>
        </div>
        <div className="space-y-0.5 p-3">
          <p className="truncate text-sm font-medium">{photo.title}</p>
          <p className="truncate text-xs text-cn-muted-foreground">
            {photo.photographer.name}
          </p>
        </div>
      </button>

      {selectable && !moderating && (
        <div className="p-3">
          <Checkbox
            checked={selected}
            disabled={disabled}
            onCheckedChange={() => onToggleSelect(photo.id)}
            aria-label={`Select ${photo.title}`}
            className="border-white bg-white/90"
          />
        </div>
      )}
      {moderating && onChoice && (
        <div className="p-2">
          <ModerationChoices
            value={
              ignoredIds.includes(photo.id)
                ? 'ignore'
                : selected
                  ? 'approved'
                  : 'rejected'
            }
            disabled={disabled}
            onChange={(choice) => onChoice(photo.id, choice)}
          />
        </div>
      )}
      {onPreview && !moderating && (
        <button
          type="button"
          className="px-3 pb-2 text-xs underline"
          disabled={disabled}
          onClick={() => onOpen(photo)}
        >
          Details
        </button>
      )}
    </div>
  );
};

export default PhotoCard;
